# Apply with SEEK

This use case provides technical implementation details for the Apply with SEEK product.
More information can be found on the [Apply with SEEK product page].

Apply with SEEK allows candidates to pre-fill an external apply form hosted by your recruitment software using information from their SEEK Profile.
Candidates can also attach their CV already stored on SEEK, making it easier to apply on desktop or mobile.

SEEK Profile information available through Apply with SEEK is only to be used to pre-fill an apply form where a candidate can review and edit the form before submitting.
It must not be used for any other purpose, such as authenticating candidates, creating user accounts, or automatically submitting an application on a candidate’s behalf.

If you are upgrading from the previous version of Apply with SEEK,
review this use case documentation and the [revision guide].

[Apply with SEEK product page]: https://au.employer.seek.com/partners/connect-with-seek#applications-soa-awsk-heading
[revision guide]: /guides/2025-revision

## Before you begin

Your software requires an Apply with SEEK relationship with a [test hirer account] to build and test the Apply with SEEK use case.
Review the [Developer Dashboard hirers page] and [contact SEEK’s support team] to configure the relationship if it is missing.

GraphQL operations for Apply with SEEK require a [partner token](/auth/partner-tokens).
[Browser tokens](/auth/browser-tokens) are not supported for Apply with SEEK operations as their restricted scope is designed to make them safe to send to authenticated hirer users in your frontend.

Once you have completed your internal build and test phases,
contact SEEK’s support team to certify your integration and grant access to Apply with SEEK for production hirer accounts.
This is a separate pre-release step to ensure integration compatibility and quality.

[contact SEEK’s support team]: https://au.employer.seek.com/contactus
[Developer Dashboard hirers page]: /manage/hirers
[test hirer account]: /introduction#integration-process

## Candidate experience

### Overview

Apply with SEEK allows candidates to use their SEEK Profile data when applying for jobs.
For example, when they [link out] from a SEEK job ad to your apply form, they can be presented with the following flows:

```mermaid Candidate Overview
sequenceDiagram
  autonumber
  participant Candidate
  participant SEEK
  participant Partner

  Candidate->>SEEK: View job ad
  Candidate->>SEEK: Click Apply button

  alt manual button flow
    SEEK-->>Candidate: Redirect to partner apply form
    Candidate->>Partner: Click Apply with SEEK button
    Partner-->>Candidate: Redirect to Apply with SEEK authorization page
  else pre-authorization flow
    SEEK-->>Candidate: Redirect to Apply with SEEK authorization page
  end

  Candidate->>SEEK: Click Allow access button
  SEEK-->>Candidate: Redirect to partner with pre-filled form fields
  Candidate->>Partner: Review and edit pre-filled form fields
  Candidate->>Partner: Click Submit button
```

Apply with SEEK also works for candidates that arrive on your application form directly or through another channel.
The experience remains similar but they are only afforded the manual button flow.

[Link out]: /use-cases/job-posting/application-methods#linking-out-to-an-external-apply-form

### Implementation flows

Your software must support both of the below flows to be certified for Apply with SEEK:

1. Manual button flow: The traditional approach where candidates click a button on your form to authorize SEEK Profile access
2. Pre-authorization flow: A streamlined approach where authorization happens before candidates reach your form

#### Manual button flow

```mermaid
flowchart LR

A1(SEEK job ad)
A2(direct)
A3(another channel)
B[empty apply form]
C[SEEK authorization page]
D(pre-filled apply form)

A1 --> B
A2 --> B
A3 --> B
B --> C
C --> D
```

How it works:

1. The candidate clicks the Apply button on SEEK.
2. The candidate is redirected to the external apply form.
3. If they click the Apply with SEEK button on the form,
   they are prompted to temporarily authorize access to their SEEK Profile.
4. Your software can pre-fill the form using their profile data after this exchange.
5. The candidate submits their application on the external form.

The previous version of Apply with SEEK worked in this way,
and it remains the default interaction model.

#### Pre-authorization flow

```mermaid
flowchart LR

A(SEEK job ad)
C[SEEK authorization page]
D(pre-filled apply form)

A --> C
C --> D
```

How it works:

1. The candidate clicks the Apply button on SEEK.
2. The authorization page is presented to the candidate upfront,
   before they are redirected to the external apply form.
3. Your software can pre-fill the form upon redirect.
4. The candidate submits their application on the external form.

This streamlines the candidate’s experience when they start on SEEK,
and is planned to be selectively enabled on suitable job ads over time.

## Implementation steps

To enable Apply with SEEK on your apply form, the following steps are required:

1. [Display button]
2. [Handle authorization]
3. [Retrieve SEEK Profile]
4. [Download resume]
5. [Populate form]
6. [Send application complete signal]

[Display button]: /use-cases/apply-with-seek/display-button
[Download resume]: /use-cases/apply-with-seek/download-resume
[Handle authorization]: /use-cases/apply-with-seek/handle-authorization
[Populate form]: /use-cases/apply-with-seek/populate-form
[Retrieve SEEK Profile]: /use-cases/apply-with-seek/retrieve-seek-profile
[Send application complete signal]: /use-cases/apply-with-seek/send-application-complete-signal

The diagram below shows an expanded sequence of a candidate applying via Apply with SEEK.
It details the interactions between your software and SEEK to pre-fill an apply form,
grouped by each step in the integration process.

```mermaid Full sequence
sequenceDiagram
participant Candidate
participant SEEK
participant Partner
participant SEEKAPI as SEEK API

  Candidate->>SEEK: View job ad
  Candidate->>SEEK: Click Apply button

rect
  alt manual button flow
    Note right of Partner: Step 1
    SEEK->>Partner: Redirect to partner apply form<br />(seek-token)
    activate Partner
    Partner->>SEEKAPI: Request Apply with SEEK button<br />(seek-token)
    Partner-->>Candidate: Display form with Apply with SEEK button
    deactivate Partner
    Candidate->>Partner: Click Apply with SEEK button
    activate Partner
    Partner-->>Candidate: Redirect to Apply with SEEK authorization page
    deactivate Partner
  else pre-authorization flow
    SEEK-->>Candidate: Redirect to Apply with SEEK authorization page
  end
end

rect
  Note right of Partner: Step 2
  Candidate->>SEEK: Click Allow access button

  SEEK->>Partner: Redirect to partner apply form<br />(seek-prefill-id + seek-token)
  activate Partner
end

rect
  Note right of Partner: Step 3
  Partner->>SEEKAPI: Retrieve SEEK Profile<br />(seek-prefill-id)
end

rect
  Note right of Partner: Step 4
  Partner->>SEEKAPI: Download resume
end

rect
  Note right of Partner: Step 5
  Partner-->>Candidate: Populate form
  deactivate Partner
end

rect
  Note right of Partner: Step 6
  Candidate->>Partner: Submit application data
  activate Partner
  Partner->>SEEKAPI: Send application complete signal<br />(seek-token)
  deactivate Partner
end
```
